import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Trophy, Eye, EyeSlash, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import apiClient from "../lib/api";
import GcrLogo from "../components/GcrLogo";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Enlace inválido. Solicita uno nuevo.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
    if (password !== confirm) return setError("Las contraseñas no coinciden");
    setLoading(true);
    try {
      await apiClient.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 3500);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al restablecer la contraseña. Intenta solicitar un nuevo enlace.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-8 h-8 rounded-lg bg-yellow-400 flex items-center justify-center">
            <Trophy weight="fill" className="w-4 h-4 text-black" />
          </div>
          <GcrLogo size={34} />
        </div>

        {done ? (
          <div data-testid="reset-success" className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle weight="fill" className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-display font-black text-2xl tracking-tight mb-3">
              ¡Contraseña actualizada!
            </h2>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Serás redirigido al login en unos segundos...
            </p>
          </div>
        ) : (
          <>
            <div className="mb-7">
              <h1 className="text-display font-black text-3xl tracking-tight mb-2">
                Nueva contraseña
              </h1>
              <p className="text-sm text-zinc-500">
                Elige una contraseña segura para tu cuenta.
              </p>
            </div>

            {error && !token ? (
              <div data-testid="reset-invalid-token"
                className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
                <WarningCircle weight="fill" className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-sm text-red-400">Enlace inválido</div>
                  <div className="text-xs text-zinc-500 mt-1">{error}</div>
                  <Link to="/forgot" className="text-xs text-yellow-400 hover:text-yellow-300 font-semibold mt-2 inline-block">
                    Solicitar nuevo enlace →
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest font-bold block mb-1.5">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-input"
                      type={showPwd ? "text" : "password"}
                      className="input-field w-full pr-10"
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoFocus
                    />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                      {showPwd ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest font-bold block mb-1.5">
                    Confirmar contraseña
                  </label>
                  <input
                    data-testid="reset-confirm-input"
                    type={showPwd ? "text" : "password"}
                    className="input-field w-full"
                    placeholder="Repite la contraseña"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                  />
                </div>

                {error && (
                  <p data-testid="reset-error" className="text-red-400 text-sm font-medium">{error}</p>
                )}

                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading || !token}
                  className="btn-primary w-full"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Actualizando...
                    </span>
                  ) : "Restablecer contraseña"}
                </button>
              </form>
            )}
          </>
        )}

        <div className="mt-8 text-center">
          <Link to="/login" className="text-sm text-zinc-500 hover:text-white transition-colors">
            Volver al login
          </Link>
        </div>
      </div>
    </div>
  );
}
